import { useCallback, useEffect, useState } from 'react';
import { writeXlsx } from '../../reports/engine';
import { auditService } from '../../services/auditService';
import { downloadFile, fmtMoney, fmtQty, safeFileName, todayStamp, XLSX_TYPE } from '../Reports/ui';
import { RECOUNT_STATUS } from './RecountPanel';
import { ArticleTag } from './ComparisonPanel';
import DataGrid, { numberColumn } from '../../components/DataGrid';
import { PageLoader, useBusyLoader } from '../../components/Loader';

const PAGE = 100;

const TYPE_LABEL = {
  shortage: 'Shortage',
  excess: 'Excess',
  not_in_book: 'Not in book',
};

/**
 * Discrepancy alerts: items whose scanned quantity differs from book stock
 * by at least the audit's limits. Each can be sent for a joint recount.
 */
function DiscrepancyPanel({ auditId, auditName, canManage, refreshKey, onRequestRecount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [action, setAction] = useState('');
  const [type, setType] = useState('');
  const [sort, setSort] = useState('qty');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [limits, setLimits] = useState({ min_qty: '1', min_value: '' });
  const [exporting, setExporting] = useBusyLoader('Preparing Excel file…');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    try {
      const result = await auditService.getDiscrepancies(auditId, {
        action, type, sort, search, limit: PAGE, offset,
      });
      setData(result);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId, action, type, sort, search, offset]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (data?.thresholds && !limitsOpen) {
      setLimits({
        min_qty: String(data.thresholds.min_qty ?? 1),
        min_value: data.thresholds.min_value === null || data.thresholds.min_value === undefined ? '' : String(data.thresholds.min_value),
      });
    }
  }, [data, limitsOpen]);

  const saveLimits = async (event) => {
    event.preventDefault();
    try {
      await auditService.setDiscrepancyLimits(auditId, {
        min_qty: Number(limits.min_qty || 0),
        min_value: limits.min_value === '' ? null : Number(limits.min_value),
      });
      setLimitsOpen(false);
      setOffset(0);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const all = await auditService.getDiscrepancies(auditId, { action, type, sort, search, all: '1' });
      const bytes = await writeXlsx([
        {
          name: 'Discrepancies',
          title: [`${auditName} — Discrepancy alerts`],
          notes: [
            `Items whose scanned quantity differs from book stock by at least ${fmtQty(all.thresholds.min_qty)} units${all.thresholds.min_value ? ` and ${fmtMoney(all.thresholds.min_value)} in value` : ''}. Generated ${new Date().toLocaleString()}.`,
          ],
          columns: [
            { label: 'EAN', key: 'ean', type: 'text', width: 16 },
            { label: 'Counted As', key: 'by_article', type: 'text', value: (r) => (r.by_article ? 'Article (all its EANs)' : 'EAN') },
            { label: 'Article', key: 'article', type: 'text' },
            { label: 'Description', key: 'description', type: 'text', width: 34 },
            { label: 'Category', key: 'category', type: 'text' },
            { label: 'Type', key: 'status', type: 'text', value: (r) => TYPE_LABEL[r.status] || r.status },
            { label: 'Book Qty', key: 'book_qty', type: 'qty' },
            { label: 'Scanned Qty', key: 'scanned_qty', type: 'qty' },
            { label: 'Difference', key: 'variance', type: 'qty' },
            { label: 'MRP', key: 'mrp', type: 'money' },
            { label: 'Difference Value', key: 'difference_value', type: 'money' },
            { label: 'Scanned At (locations)', key: 'locations', type: 'text', width: 30, value: (r) => (r.locations || []).map((l) => `${l.location} (${fmtQty(l.qty)})`).join(', ') },
            { label: 'Recount', key: 'action', type: 'text', value: (r) => (r.recount ? RECOUNT_STATUS[r.recount.status]?.label || r.recount.status : '') },
          ],
          rows: all.items,
        },
      ]);
      downloadFile(`${safeFileName(auditName)} - discrepancies ${todayStamp()}.xlsx`, bytes, XLSX_TYPE);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      headerName: 'EAN',
      field: 'ean',
      minWidth: 170,
      pinned: 'left',
      cellRenderer: (p) => (
        <>
          {p.value}
          {p.data.by_article ? <ArticleTag /> : null}
        </>
      ),
    },
    {
      headerName: 'Description',
      field: 'description',
      minWidth: 220,
      flex: 2,
      cellRenderer: (p) => (
        <>
          {p.data.description || '—'}
          {p.data.article ? <small className="live-muted"> · {p.data.article}</small> : null}
        </>
      ),
    },
    {
      headerName: 'Type',
      field: 'status',
      minWidth: 130,
      valueGetter: (p) => TYPE_LABEL[p.data.status] || p.data.status,
      cellRenderer: (p) => (
        <span
          className={`compare-pill compare-pill--${
            p.data.status === 'excess' ? 'excess' : p.data.status === 'shortage' ? 'shortage' : 'not_in_book'
          }`}
        >
          {p.value}
        </span>
      ),
    },
    numberColumn('Book', 'book_qty', fmtQty),
    numberColumn('Scanned', 'scanned_qty', fmtQty),
    {
      ...numberColumn('Difference', 'variance', (v) => `${v > 0 ? '+' : ''}${fmtQty(v)}`),
      cellClass: (p) => (p.value < 0 ? 'data-grid__neg' : 'data-grid__pos'),
    },
    numberColumn('Value', 'difference_value', fmtMoney, { empty: '—' }),
    {
      headerName: 'Scanned at',
      colId: 'locations',
      minWidth: 200,
      sortable: false,
      valueGetter: (p) =>
        p.data.locations.length
          ? p.data.locations
              .slice(0, 3)
              .map((loc) => `${loc.location} (${fmtQty(loc.qty)})`)
              .join(', ') + (p.data.locations.length > 3 ? ` +${p.data.locations.length - 3}` : '')
          : '—',
    },
    {
      headerName: 'Recount',
      colId: 'recount',
      minWidth: 170,
      sortable: false,
      filter: false,
      valueGetter: (p) => (p.data.recount ? RECOUNT_STATUS[p.data.recount.status]?.label : 'Not requested'),
      cellRenderer: (p) => {
        const item = p.data;
        const recount = item.recount ? RECOUNT_STATUS[item.recount.status] : null;
        return recount ? (
          <span className={`live-pill live-pill--${recount.tone}`}>{recount.label}</span>
        ) : (
          <button
            type="button"
            className="compare-link"
            onClick={() =>
              onRequestRecount({
                ean: item.ean,
                product_name: item.description,
                location_code: item.locations[0]?.location || '',
              })
            }
          >
            Request recount
          </button>
        );
      },
    },
  ];

  if (!data) {
    return (
      <section className="audit-portal__panel live-panel">
        {error ? <p className="compare-error">{error}</p> : <PageLoader label="Loading discrepancies…" />}
      </section>
    );
  }

  if (!data.has_book) {
    return (
      <section className="audit-portal__panel live-panel">
        <h2>Discrepancy alerts</h2>
        <p className="compare-empty">
          Discrepancies compare scanned quantities with the client's book stock. Upload the book
          stock on the Comparison tab ("Scanned vs book stock") to see them here.
        </p>
      </section>
    );
  }

  const { summary, thresholds } = data;

  return (
    <section className="audit-portal__panel live-panel">
      <header className="live-panel__header">
        <div>
          <h2>Discrepancy alerts</h2>
          <p className="live-muted">
            Items where the scanned quantity differs from book stock by at least{' '}
            <strong>{fmtQty(thresholds.min_qty)}</strong> unit{Number(thresholds.min_qty) === 1 ? '' : 's'}
            {thresholds.min_value ? (
              <>
                {' '}and <strong>{fmtMoney(thresholds.min_value)}</strong> in value
              </>
            ) : null}
            . Send any of them for a joint recount.
          </p>
        </div>
        <div className="live-panel__actions">
          {canManage ? (
            <button type="button" className="compare-button compare-button--ghost" onClick={() => setLimitsOpen((open) => !open)}>
              Alert limits
            </button>
          ) : null}
          <button type="button" className="compare-button compare-button--ghost" onClick={exportExcel} disabled={exporting}>
            {exporting ? 'Preparing…' : 'Download Excel'}
          </button>
        </div>
      </header>

      {limitsOpen ? (
        <form className="live-limits" onSubmit={saveLimits}>
          <label>
            Flag when the difference is at least (units)
            <input type="number" min="0" step="any" value={limits.min_qty} onChange={(event) => setLimits((prev) => ({ ...prev, min_qty: event.target.value }))} />
          </label>
          <label>
            …and worth at least (MRP, optional)
            <input type="number" min="0" step="any" value={limits.min_value} placeholder="Any value" onChange={(event) => setLimits((prev) => ({ ...prev, min_value: event.target.value }))} />
          </label>
          <button type="submit" className="compare-button">Save limits</button>
          <button type="button" className="compare-link" onClick={() => setLimitsOpen(false)}>Cancel</button>
          <p className="live-muted">Items without an MRP or cost stay on the list when a value limit is set.</p>
        </form>
      ) : null}

      {error ? <p className="compare-error">{error}</p> : null}

      <section className="compare-cards">
        <article className="compare-card compare-card--bad">
          <span>Items flagged</span>
          <strong>{fmtQty(summary.flagged)}</strong>
          <p>
            {fmtQty(summary.shortage_items)} short · {fmtQty(summary.excess_items)} excess · {fmtQty(summary.not_in_book_items)} not in book
          </p>
        </article>
        <article className="compare-card live-card--warn">
          <span>Awaiting recount</span>
          <strong>{fmtQty(summary.awaiting_recount)}</strong>
          <p>Recount requested, not finished yet</p>
        </article>
        <article className="compare-card compare-card--good">
          <span>Recounted</span>
          <strong>{fmtQty(summary.recounted)}</strong>
          <p>{fmtQty(summary.no_action)} flagged items have no recount yet</p>
        </article>
        <article className="compare-card">
          <span>Units short / excess</span>
          <strong>
            {fmtQty(summary.shortage_qty)} / {fmtQty(summary.excess_qty)}
          </strong>
          <p>Across the flagged items</p>
        </article>
      </section>

      <div className="compare-toolbar">
        <select value={action} onChange={(event) => { setAction(event.target.value); setOffset(0); }} aria-label="Recount state">
          <option value="">All flagged items</option>
          <option value="none">No recount yet</option>
          <option value="awaiting_recount">Awaiting recount</option>
          <option value="recounted">Recounted</option>
        </select>
        <select value={type} onChange={(event) => { setType(event.target.value); setOffset(0); }} aria-label="Type">
          <option value="">Shortage, excess &amp; not in book</option>
          <option value="shortage">Shortage only</option>
          <option value="excess">Excess only</option>
          <option value="not_in_book">Scanned, not in book</option>
        </select>
        <select value={sort} onChange={(event) => { setSort(event.target.value); setOffset(0); }} aria-label="Sort">
          <option value="qty">Biggest difference (units)</option>
          <option value="value">Biggest difference (value)</option>
        </select>
        <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search EAN, article or product…" aria-label="Search discrepancies" />
      </div>

      <DataGrid
        rowData={data.items}
        columnDefs={columns}
        getRowId={(item) => item.ean}
        search={false}
        pageSize={0}
        emptyMessage="Nothing flagged for this filter."
      />
      {data.total > PAGE ? (
        <div className="compare-pager">
          <span>
            {fmtQty(offset + 1)}–{fmtQty(Math.min(offset + PAGE, data.total))} of {fmtQty(data.total)}
          </span>
          <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
            Previous
          </button>
          <button type="button" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>
            Next
          </button>
        </div>
      ) : (
        <p className="compare-muted">{fmtQty(data.total)} items</p>
      )}
    </section>
  );
}

export default DiscrepancyPanel;
